import type { TFunction } from 'i18next';
import { z } from 'zod';

export type ContactValues = {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
};

export type OrgFormValues = {
  name: string;
  address: string;
  alias: string;
  primaryContact: ContactValues;
  itContact: ContactValues;
  domain: string;
  subscription: {
    active: boolean;
    expiration: string;
  };
};

const PHONE_REGEX = /^\+?[1-9]\d{1,14}$/;
const DOMAIN_REGEX = /^([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;
// Alias is used as a URL-safe slug — letters, numbers, and hyphens only.
const ALIAS_REGEX = /^[a-zA-Z0-9-]+$/;
// Person names — letters (incl. accented), spaces, hyphens, and apostrophes.
const PERSON_NAME_REGEX = /^[\p{L}][\p{L}\s'-]*$/u;

const NAME_MAX_LENGTH = 100;
const ALIAS_MAX_LENGTH = 50;
const ADDRESS_MAX_LENGTH = 500;
const PERSON_NAME_MAX_LENGTH = 50;

/** Adds a single "required" or "too long" issue for a free-text field, trimming before checking. Callers add any additional format checks after calling this since only one issue path is added here. */
function checkRequiredAndLength(
  ctx: z.RefinementCtx,
  value: string,
  path: (string | number)[],
  maxLength: number,
  messages: { required: string; tooLong: string },
): boolean {
  const trimmed = value.trim();
  if (!trimmed) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: messages.required, path });
    return false;
  }
  if (trimmed.length > maxLength) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: messages.tooLong, path });
    return false;
  }
  return true;
}

export function createOrgSchema(t: TFunction<'orgs'>) {
  const contactSchema = z
    .object({
      firstName: z.string(),
      lastName: z.string(),
      phone: z.string().regex(PHONE_REGEX, t('validation_phone_invalid')),
      email: z.string().email(t('validation_email_invalid')),
    })
    .superRefine((val, ctx) => {
      (['firstName', 'lastName'] as const).forEach(field => {
        const requiredKey = field === 'firstName' ? 'validation_first_name_required' : 'validation_last_name_required';
        const ok = checkRequiredAndLength(ctx, val[field], [field], PERSON_NAME_MAX_LENGTH, {
          required: t(requiredKey),
          tooLong: t('validation_person_name_max_length'),
        });
        if (ok && !PERSON_NAME_REGEX.test(val[field].trim())) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: t('validation_person_name_invalid'), path: [field] });
        }
      });
    });

  return z.object({
    // A leaf-level superRefine's ctx is already scoped to this field, so issue paths here are
    // relative (`[]`) — an explicit `['name']` would double up to `['name', 'name']` and the
    // error would never attach to the actual field.
    name: z.string().superRefine((val, ctx) => {
      const ok = checkRequiredAndLength(ctx, val, [], NAME_MAX_LENGTH, {
        required: t('validation_name_required'),
        tooLong: t('validation_name_max_length'),
      });
      if (ok && val.trim().length < 3) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: t('validation_name_min_length'), path: [] });
      }
    }),
    address: z.string().superRefine((val, ctx) => {
      checkRequiredAndLength(ctx, val, [], ADDRESS_MAX_LENGTH, {
        required: t('validation_address_required'),
        tooLong: t('validation_address_max_length'),
      });
    }),
    alias: z.string().superRefine((val, ctx) => {
      const ok = checkRequiredAndLength(ctx, val, [], ALIAS_MAX_LENGTH, {
        required: t('validation_alias_required'),
        tooLong: t('validation_alias_max_length'),
      });
      if (!ok) return;
      if (val.trim().length < 2) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: t('validation_alias_min_length'), path: [] });
      } else if (!ALIAS_REGEX.test(val.trim())) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: t('validation_alias_invalid'), path: [] });
      }
    }),
    primaryContact: contactSchema,
    itContact: contactSchema,
    domain: z.string().min(1, t('validation_domain_required')).regex(DOMAIN_REGEX, t('validation_domain_invalid')),
    subscription: z
      .object({
        active: z.boolean(),
        expiration: z.string(),
      })
      .superRefine((val, ctx) => {
        if (val.active && !val.expiration) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('validation_expiration_required'),
            path: ['expiration'],
          });
        }
      }),
  });
}
